/**
 * Gallery Slider — frontend Swiper initialization and video controls.
 *
 * @package
 */

/**
 * Sync play/pause button UI with the actual video state.
 *
 * @param {HTMLButtonElement} button Play/pause toggle button.
 * @param {boolean}          isPlaying Whether the video is currently playing.
 */
const syncPlayButtonState = ( button, isPlaying ) => {

	button.classList.toggle( "is-playing", isPlaying );
	button.setAttribute( "aria-pressed", isPlaying ? "true" : "false" );

	const playLabel = button.dataset.labelPlay || "Odtwórz film";
	const pauseLabel = button.dataset.labelPause || "Wstrzymaj film";

	button.setAttribute( "aria-label", isPlaying ? pauseLabel : playLabel );

};

/**
 * Sync audio button UI with the actual muted state.
 *
 * @param {HTMLButtonElement} button Audio toggle button.
 * @param {boolean}          isMuted Whether the video is muted.
 */
const syncAudioButtonState = ( button, isMuted ) => {

	button.classList.toggle( "is-muted", isMuted );
	button.setAttribute( "aria-pressed", isMuted ? "true" : "false" );

	const mutedLabel = button.dataset.labelMuted || "Włącz dźwięk";
	const unmutedLabel = button.dataset.labelUnmuted || "Wycisz film";

	button.setAttribute( "aria-label", isMuted ? mutedLabel : unmutedLabel );

};

/**
 * Stop all videos in the block and reset them to initial state.
 *
 * Used on slide change — full reset (currentTime = 0, muted, controls back to default).
 *
 * @param {HTMLElement} block Slider block root element.
 */
const resetAllVideos = ( block ) => {

	const slides = block.querySelectorAll( ".b-gallery-slider__slide" );

	slides.forEach( ( slide ) => {

		const video = slide.querySelector( ".js-gallery-slider-video" );

		if ( !video ) {

			return;

		}

		video.pause();
		video.currentTime = 0;
		video.muted = true;
		video.defaultMuted = true;

		slide.classList.remove( "b-gallery-slider__slide--video-playing" );

		const bigPlay = slide.querySelector( ".js-gallery-slider-big-play" );

		if ( bigPlay ) {

			bigPlay.hidden = false;

		}

		const playButton = slide.querySelector( ".js-gallery-slider-play" );

		if ( playButton ) {

			syncPlayButtonState( playButton, false );

		}

		const audioButton = slide.querySelector( ".js-gallery-slider-audio" );

		if ( audioButton ) {

			syncAudioButtonState( audioButton, true );

		}

	} );

};

/**
 * Start playing a video in a given slide.
 *
 * @param {HTMLElement} block Slider block root element.
 * @param {HTMLElement} slide The slide element containing the video.
 */
const playVideo = ( block, slide ) => {

	const video = slide.querySelector( ".js-gallery-slider-video" );

	if ( !video ) {

		return;

	}

	// If starting fresh (from initial state), reset time.
	if ( video.currentTime === 0 ) {

		video.currentTime = 0;

	}

	video
		.play()
		.then( () => {

			slide.classList.add(
				"b-gallery-slider__slide--video-playing",
			);

			const bigPlay = slide.querySelector(
				".js-gallery-slider-big-play",
			);

			if ( bigPlay ) {

				bigPlay.hidden = true;

			}

			const playButton = slide.querySelector(
				".js-gallery-slider-play",
			);

			if ( playButton ) {

				syncPlayButtonState( playButton, true );

			}

		} )
		.catch( () => {
			// Playback blocked by browser.
		} );

};

/**
 * Pause a video in a given slide (without resetting position).
 *
 * @param {HTMLElement} slide The slide element containing the video.
 */
const pauseVideo = ( slide ) => {

	const video = slide.querySelector( ".js-gallery-slider-video" );

	if ( !video ) {

		return;

	}

	video.pause();

	slide.classList.remove( "b-gallery-slider__slide--video-playing" );

	const playButton = slide.querySelector( ".js-gallery-slider-play" );

	if ( playButton ) {

		syncPlayButtonState( playButton, false );

	}

};

/**
 * Bind play/pause, big play, and audio controls for all video slides.
 *
 * @param {HTMLElement} block Slider block root element.
 */
const bindVideoControls = ( block ) => {

	// Big centered play button — starts video from beginning.
	const bigPlayButtons = block.querySelectorAll(
		".js-gallery-slider-big-play",
	);

	bigPlayButtons.forEach( ( bigPlay ) => {

		bigPlay.addEventListener( "click", () => {

			const slide = bigPlay.closest( ".b-gallery-slider__slide" );

			if ( !slide ) {

				return;

			}

			const video = slide.querySelector( ".js-gallery-slider-video" );

			if ( video ) {

				video.currentTime = 0;

			}

			playVideo( block, slide );

		} );

	} );

	// Small corner play/pause toggle.
	const playButtons = block.querySelectorAll( ".js-gallery-slider-play" );

	playButtons.forEach( ( playButton ) => {

		playButton.addEventListener( "click", () => {

			const slide = playButton.closest( ".b-gallery-slider__slide" );

			if ( !slide ) {

				return;

			}

			const video = slide.querySelector( ".js-gallery-slider-video" );

			if ( !video ) {

				return;

			}

			if ( video.paused ) {

				playVideo( block, slide );

			} else {

				pauseVideo( slide );

			}

		} );

	} );

	// Audio mute/unmute toggle.
	const audioButtons = block.querySelectorAll( ".js-gallery-slider-audio" );

	audioButtons.forEach( ( audioButton ) => {

		audioButton.addEventListener( "click", () => {

			const slide = audioButton.closest( ".b-gallery-slider__slide" );

			if ( !slide ) {

				return;

			}

			const video = slide.querySelector( ".js-gallery-slider-video" );

			if ( !video ) {

				return;

			}

			const nextMuted = !video.muted;

			video.muted = nextMuted;
			video.defaultMuted = nextMuted;
			syncAudioButtonState( audioButton, nextMuted );

		} );

	} );

};

/**
 * Initialise Swiper instances for all gallery sliders.
 */
const initGallerySliders = () => {

	const { Swiper, Pagination, EffectFade, Autoplay, Navigation, A11y } =
		window.WombatSwiper;

	const blocks = document.querySelectorAll( ".b-gallery-slider" );

	blocks.forEach( ( block ) => {

		const swiperEl = block.querySelector( ".js-gallery-slider-swiper" );

		if ( !swiperEl ) {

			return;

		}

		bindVideoControls( block );

		const isAutoplay = block.dataset.autoplay === "true";
		const delay = parseInt( block.dataset.delay, 10 ) || 5000;

		const modules = [Pagination, EffectFade, Navigation, A11y];

		if ( isAutoplay ) {

			modules.push( Autoplay );

		}

		/**
		 * Handle slide change — reset all videos to initial state.
		 */
		const handleSlideChangeStart = () => {

			resetAllVideos( block );

		};

		const config = {
			modules,
			effect: "fade",
			fadeEffect: {
				crossFade: true,
			},
			loop: block.querySelectorAll( ".b-gallery-slider__slide" ).length > 1,
			speed: 650,
			touchRatio: 1,
			slidesPerView: 1,
			navigation: {
				prevEl: block.querySelector( ".b-gallery-slider__arrow--prev" ),
				nextEl: block.querySelector( ".b-gallery-slider__arrow--next" ),
			},
			pagination: {
				el: block.querySelector( ".b-gallery-slider__pagination" ),
				clickable: true,
				bulletClass: "b-gallery-slider__dot",
				bulletActiveClass: "b-gallery-slider__dot--active",
			},
			a11y: {
				enabled: true,
				prevSlideMessage: "Poprzedni slajd",
				nextSlideMessage: "Następny slajd",
				paginationBulletMessage: "Przejdź do slajdu {{index}}",
			},
			on: {
				slideChangeTransitionStart: handleSlideChangeStart,
			},
		};

		if ( isAutoplay ) {

			config.autoplay = {
				delay,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			};

		}

		// eslint-disable-next-line no-new
		new Swiper( swiperEl, config );

	} );

};

if ( document.readyState === "loading" ) {

	document.addEventListener( "DOMContentLoaded", initGallerySliders );

} else {

	initGallerySliders();

}
